// src/pages/meeting/MeetingFutureList.js
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Spinner from "../../components/Spinner/Spinner";
import { getFutureMeetings } from "../../features/meeting/meetingSlice";
import {
  formatDateTime,
  meetingTypeLabel,
  toDate,
  isActivityMeeting,
  getDetailPath,
} from "../../utils/meetingUtils";

const isFuture = (d) => {
  const dt = toDate(d);
  return dt ? dt.getTime() >= Date.now() : false;
};
const isVisibleMeeting = (m) => m?.state !== false && m?.state !== 'false';
const isEvent = (m) => isActivityMeeting(m);

const SectionHeader = ({ title, linkTo, linkLabel }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ width: "4px", height: "22px", borderRadius: "4px", backgroundColor: "#5f60b9" }} />
      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "1rem", color: "#1a1a2e" }}>{title}</h5>
    </div>
    <Link to={linkTo} style={{ fontSize: "0.8rem", color: "#5f60b9", textDecoration: "none", fontWeight: 500, whiteSpace: "nowrap" }}>
      {linkLabel} &raquo;
    </Link>
  </div>
);

const CreateMeetingCard = () => (
  <Link
    to="/MeetingAdd"
    style={{
      background: "#fff",
      borderRadius: "14px",
      padding: "24px 16px",
      textAlign: "center",
      color: "#5f60b9",
      fontSize: "0.92rem",
      fontWeight: 700,
      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      display: "block",
      textDecoration: "none",
      border: "1px dashed #c8c9f0",
    }}
  >
    <i className="fa fa-plus-circle" style={{ fontSize: "24px", marginBottom: "8px", display: "block" }} />
    Toplantı Oluştur
  </Link>
);

const InfoRow = ({ icon, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
    <i className={`fa fa-${icon}`} style={{ color: "#5f60b9", fontSize: "13px", width: "14px", textAlign: "center" }} />
    <span style={{ color: "#555", fontSize: "0.82rem" }}>{text}</span>
  </div>
);

const formatHourMinute = (value) => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

const getMeetingDurationText = (startDate, endDate) => {
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end) return "-";
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "-";
  const totalMin = Math.round(diffMs / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h} sa ${m} dk`;
  if (h > 0) return `${h} sa`;
  return `${m} dk`;
};

function MeetingFutureList() {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const isAuthenticated = !!user;

  const {
    meetings = [],
    loadingStates: meetingLoadingStates,
    errors: meetingErrors,
    message: meetingsMessage,
  } = useSelector((state) => state.meeting);
  const isMeetingsLoading = meetingLoadingStates?.getFutureMeetings;
  const isMeetingsError = meetingErrors?.getFutureMeetings;

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getFutureMeetings());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isMeetingsError) console.log(meetingsMessage);
  }, [isMeetingsError, meetingsMessage]);

  const formatDate = (date) => formatDateTime(date);

  const { upcomingMeetingsAll } = useMemo(() => {
    try {
      const safeMeetings = Array.isArray(meetings) ? meetings : [];
      const upcomingMeetings = [...safeMeetings].filter((m) => isVisibleMeeting(m) && isFuture(m?.startDate));
      upcomingMeetings.sort((a, b) => {
        const da = toDate(a?.startDate)?.getTime() ?? 0;
        const db = toDate(b?.startDate)?.getTime() ?? 0;
        return da - db;
      });

      const onlyMeetings = upcomingMeetings.filter((m) => !isEvent(m));

      return {
        upcomingMeetingsAll: onlyMeetings,
      };
    } catch (err) {
      console.error("MeetingFutureList memo parse error:", err);
      return { upcomingMeetingsAll: [] };
    }
  }, [meetings]);

  if (isMeetingsLoading) return <Spinner />;
  const hasLoadError = Boolean(isMeetingsError);

  const cardStyle = {
    background: "#fff",
    borderRadius: "14px",
    padding: "16px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
    borderLeft: "4px solid #5f60b9",
    textDecoration: "none",
    display: "block",
    transition: "box-shadow 0.2s",
  };

  const MeetingCard = ({ item }) => {
    const detailPath = getDetailPath(item);
    const Content = (
      <>
      <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", marginBottom: "8px" }}>
        {item?.title || "Başlıksız Toplantı"}
      </div>
      {String(item?.status ?? '0') === '1' && (
        <div style={{ marginBottom: "8px" }}>
          <span className="badge light badge-danger">Toplantı İptal Edildi</span>
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: "16px",
        }}
      >
        <InfoRow icon="calendar" text={formatDate(item.startDate)} />
        <InfoRow icon="clock-o" text={`Saat: ${formatHourMinute(item.startDate)} - ${formatHourMinute(item.endDate)}`} />
        <InfoRow icon="hourglass-half" text={`Süre: ${getMeetingDurationText(item.startDate, item.endDate)}`} />
        <InfoRow icon="briefcase" text="Toplantı" />
        <InfoRow icon="star" text={meetingTypeLabel(item.type)} />
        <InfoRow icon="user" text={`${item.users?.length ?? 0} Katılımcı`} />
      </div>
      </>
    );

    if (!detailPath) {
      return <div style={cardStyle}>{Content}</div>;
    }

    return <Link to={detailPath} style={cardStyle}>{Content}</Link>;
  };

  return (
    <div style={{ padding: "4px 0" }}>
      {hasLoadError && (
        <div style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#9a3412",
          borderRadius: "12px",
          padding: "10px 12px",
          marginBottom: "16px",
          fontSize: "0.86rem",
        }}>
          Bazı toplantılar yüklenemedi. Sayfayı yenileyebilir veya biraz sonra tekrar deneyebilirsiniz.
        </div>
      )}

      <SectionHeader title="Toplantılarım" linkTo="/allmeetings" linkLabel="Tüm toplantılarım" />
      {upcomingMeetingsAll.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {upcomingMeetingsAll.map((m, idx) => (
            <MeetingCard key={m.meetingId || m.id || idx} item={m} />
          ))}
        </div>
      ) : <CreateMeetingCard />}

    </div>
  );
}

export default MeetingFutureList;
